import type { ComponentPropsWithRef } from 'react';
import type { CareerLiterval, RaceLiterval, RarityLiterval, LevelLiterval } from '@phalaworld/types/nftAttribute';
import type { NftInfo } from '@phalaworld/types/rmrk';
import type { Nft } from '@phalaworld/api/src/types';

import { useState } from 'react';
import { useAtomValue } from 'jotai';
import tw, { styled } from 'twin.macro';
import { motion } from 'framer-motion';
import { Box, Button } from '@chakra-ui/react';
import Link from 'next/link';
import * as R from 'ramda';
import BN from 'bn.js';

import { trimAddress } from '@phalaworld/utils';

import { RACE_TYPE_CHAIN_MAP, CAREER_TYPE_CHAIN_MAP, LEVEL_TYPE_CHAIN_MAP,  } from '@/types/nftAttribute';
import { CAREER_DATA } from '@/pages/mint/staticData/careerData';
import { RACE_DATA } from '@/pages/mint/staticData/raceData';
import { officialHatchTimeAtom } from '@/features/pwIncubation/atom';
import useCountdown from '@/hooks/useCountdown';
import { imgproxy_thumbnail_url } from '@/features/images/imgproxy';
import { formatCurrency } from '@/utils/formatCurrency';

import FoodIcon from '../../svgs/food-default.svg'

export interface Shell {
  collectionId: number;
  assetId: number;
  nftId: number;
  career: CareerLiterval;
  race: RaceLiterval;
  rarity: RarityLiterval;
  generation: number;
  hasStartedIncubation?: boolean;
  fedCounts?: number;
  metadata: {
    name: string;
    description: string;
    externalUri: string;
    backgroundUri: string;
    'backgroundUri@2x': string;
    'backgroundUri@3x': string;
    mediaUri: string;
    'mediaUri@2x': string;
    'mediaUri@3x': string;
    videoUri: string;
    webmUri: string;
  };
  info?: NftInfo | null
  originShellNftId?: number
}

const InfoPanel = styled(motion.div)`
  ${tw`relative`}
  background-color: rgba(11,13,16,0.75);
  backdrop-filter: blur(5px);
  padding: 12px 14px;
  z-index: 0;
  user-select: none;
`;

const CountdownContainer = styled(motion.div)`
  ${tw`text-brand text-sm flex flex-row justify-center gap-0.5`}
  .number {
    ${tw`px-1 py-0.5 w-4 inline-block text-center`}
    background: rgba(11, 13, 16, 0.75);
    border-radius: 2px;
  }
`

type HatchCountdownProps = Omit<ComponentPropsWithRef<typeof CountdownContainer>, 'as'>

const HatchCountdown = (props: HatchCountdownProps) => {
  const officialHatchTime = useAtomValue(officialHatchTimeAtom)
  const timer = useCountdown(officialHatchTime, false)
  const hours = timer.hours < 10 ? `0${timer.hours}` : `${timer.hours}`
  const minutes = timer.minutes < 10 ? `0${timer.minutes}` : `${timer.minutes}`
  const seconds = timer.seconds < 10 ? `0${timer.seconds}` : `${timer.seconds}`
  return (
    <CountdownContainer {...props}>
      {R.split('', hours).map((char, idx) => (
        <span className='number' key={idx}>{char}</span>
      ))}
      <span>:</span>
      <span className='number'>{minutes[0]}</span>
      <span className='number'>{minutes[1]}</span>
      <span>:</span>
      <span className='number'>{seconds[0]}</span>
      <span className='number'>{seconds[1]}</span>
    </CountdownContainer>
  )
}

export default function ShellCard({ career, race, rarity, generation, metadata, nftId, collectionId, hasStartedIncubation, fedCounts, info, hatched, originShellNftId }: Shell & {
  hatched?: boolean
}) {
  const [isHover, setIsHover] = useState(false);
  const url = `/collections/${collectionId}/${nftId}`
  return (
    <Link href={url} legacyBehavior>
      <Box
        cursor='pointer'
        border="1px solid"
        borderRadius="2px"
        borderColor={`${rarity.toLowerCase()}.500`}
        background="linear-gradient(180deg, rgba(91, 91, 91, 0.2) 0%, rgba(146, 146, 146, 0) 100%)"
        position="relative"
        minWidth="160px"
        minHeight="240px"
        css="aspect-ratio: 2/3"
        onMouseOver={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        {(info && hatched) ? (
          originShellNftId ? (
            <img src={imgproxy_thumbnail_url(`local:///shells/shell-${originShellNftId}.png`, 350, 800, 'png')} tw="h-full mx-auto" />
          ) : (
            <img src={imgproxy_thumbnail_url(info.metadata.initialAppearanceURL, 350, 800, 'webp')} tw="h-full mx-auto" />
          )
        ) : (
          <img src={metadata.mediaUri} tw="w-full h-full" />
        )}
        {/* {hasStartedIncubation ? (
          <ConditionallyRender client>
            <Suspense>
              <HatchCountdown
                tw='absolute bottom-[calc(50% + 20px)] w-full'
                initial={false} animate={{ opacity: isHover ? 1 : 0 }}
              />
            </Suspense>
          </ConditionallyRender>
        ) : null} */}
        <div css={[tw`absolute bottom-0 left-0 w-full overflow-hidden`]}>
          <InfoPanel initial={false} animate={{ translateY: isHover ? 0 : '100%' }}>
            <div tw='flex flex-row justify-between flex-wrap'>
              <div tw='mb-2'>
                <h5 tw="text-base font-bold">{RACE_TYPE_CHAIN_MAP[race]}</h5>
                <p tw="text-sm">{CAREER_TYPE_CHAIN_MAP[career]}</p>
              </div>
              {!hatched ? (
                hasStartedIncubation ? (
                  // <div tw='text-xs text-[#84BFB5] mt-1'>Incubating</div>
                  <div tw='text-xs text-[#84BFB5] mt-1'>Waiting to be Sent</div>
                ) : (
                  <div tw='text-xs text-[#957554] mt-1'>To be incubated</div>
                )) : null}
            </div>
            <div tw="flex flex-row justify-between mt-2 text-sm text-grey-300">
              <span>Gen {generation}</span>
              {fedCounts ? (
                <span tw='inline-flex flex-row gap-1 items-center'>
                  <FoodIcon tw='h-3 w-3 text-brand' />{fedCounts}
                </span>
              ) : (<span />)}
            </div>
          </InfoPanel>
          <Box
            padding="0.25rem 0.25rem"
            backgroundColor={`${rarity.toLowerCase()}.500`}
            zIndex="2"
            position="relative"
            tw="flex flex-row justify-between"
          >
            <span tw="text-xs">{rarity}</span>
            <span tw="text-xs">#{nftId}</span>
          </Box>
        </div>
      </Box>
    </Link>
  );
}

export interface UINft {
  id: number
  previewImages?: { background?: string, character: string }
  owner: {
    accountId: string
    displayName: string
  }
  collection: {
    id: number
    name: string
    description: string
  }
  generation: number
  rarity: {
    slug: LevelLiterval
    displayName: string
  }
  race: {
    slug: RaceLiterval
    displayName: string
    description: string
  }
  career: {
    slug: CareerLiterval
    displayName: string
    description: string
  }
  listed?: {
    price: BN
    listedAt: Date
  }
}

export function transformUINft(nftInfo: Nft) {
  // For different NFT we have different previews.
  let previewImages
  if (nftInfo.originShellNftId !== undefined) {
    previewImages = {} as { background?: string, character: string }
    previewImages.character = `local:///shells/shell-${nftInfo.originShellNftId}.png`
    if (nftInfo.backgroundImageUrl) {
      previewImages.background = nftInfo.backgroundImageUrl!
    }
  }
  if (nftInfo.collectionId === 1 && nftInfo.imageUrl) {
    previewImages = {} as { background?: string, character: string }
    previewImages.character = nftInfo.imageUrl
    if (nftInfo.backgroundImageUrl) {
      previewImages.background = nftInfo.backgroundImageUrl!
    }
  }

  // const isOwner = currentAccount?.address === accountId
  return {
    id: nftInfo.nftId,
    previewImages,
    // metadataUrl: nftInfo?.metadataUrl,
    owner: {
      accountId: nftInfo.ownerId,
      displayName: nftInfo.ownerDisplayName || trimAddress(nftInfo.ownerId || ''),
      // isOwner,
    },
    rarity: {
      slug: nftInfo.rarity as LevelLiterval,
      displayName: LEVEL_TYPE_CHAIN_MAP[nftInfo.rarity],
    },
    generation: Number(R.pathOr('', ['metadata', 'generation'], nftInfo).replace('Gen', '')),
    collection: {
      id: nftInfo.collectionId,
      name: 'Shell',
      description: '8,888 limited edition NFTs. PhalaWorld Shells are the first generation of  bodies.',
    },
    race: {
      slug: nftInfo.race as RaceLiterval,
      displayName: RACE_TYPE_CHAIN_MAP[nftInfo.race],
      description: RACE_DATA[RACE_TYPE_CHAIN_MAP[nftInfo.race]]['desc'],
    },
    career: {
      slug: nftInfo.career as CareerLiterval,
      displayName: CAREER_TYPE_CHAIN_MAP[nftInfo.career],
      description: CAREER_DATA[CAREER_TYPE_CHAIN_MAP[nftInfo.career]]['desc'],
    },
    listed: nftInfo.listedPrice ? {
      price: new BN(nftInfo.listedPrice.toString()),
      listedAt: nftInfo.listedAt,
    } : undefined,
  } as UINft
}

export function ShellCardV2({ nft }: { nft: UINft }) {
  const [isHover, setIsHover] = useState(false);
  const url = `/collections/${nft.collection.id}/${nft.id}`
  const rarityColor = nft.rarity.slug.toLowerCase() + '.500'
  return (
    <Link href={url} legacyBehavior>
      <Box
        cursor='pointer'
        border="1px solid"
        borderRadius="2px"
        borderColor={rarityColor}
        background="linear-gradient(180deg, rgba(91, 91, 91, 0.2) 0%, rgba(146, 146, 146, 0) 100%)"
        position="relative"
        minWidth="160px"
        minHeight="240px"
        css="aspect-ratio: 365/650"
        onMouseOver={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <div css={[tw`relative overflow-hidden`, 'aspect-ratio: 365/650;']}>
          {nft.previewImages?.background ? (
            <img
              src={imgproxy_thumbnail_url(nft.previewImages.background, 350, 800, 'jpg')}
              tw='h-full mx-auto absolute top-0 left-0'
            />
          ) : null}
          {nft.previewImages?.character ? (
            <img
              src={imgproxy_thumbnail_url(nft.previewImages?.character, 350, 800, 'webp')}
              tw='h-full mx-auto relative z-[1]'
            />
          ) : null}
        </div>
        {/* {(info && hatched) ? ( */}
        {/*   originShellNftId ? ( */}
        {/*     <img src={imgproxy_thumbnail_url(`local:///shells/shell-${originShellNftId}.png`, 350, 800, 'png')} tw="h-full mx-auto" /> */}
        {/*   ) : ( */}
        {/*     <img src={imgproxy_thumbnail_url(info.metadata.initialAppearanceURL, 350, 800, 'webp')} tw="h-full mx-auto" /> */}
        {/*   ) */}
        {/* ) : ( */}
        {/*   <img src={metadata.mediaUri} tw="w-full h-full" /> */}
        {/* )} */}
        {/* {hasStartedIncubation ? (
          <ConditionallyRender client>
            <Suspense>
              <HatchCountdown
                tw='absolute bottom-[calc(50% + 20px)] w-full'
                initial={false} animate={{ opacity: isHover ? 1 : 0 }}
              />
            </Suspense>
          </ConditionallyRender>
        ) : null} */}
        <div css={[tw`absolute bottom-0 left-0 w-full overflow-hidden z-[2]`]}>
          <InfoPanel initial={false} animate={{ translateY: isHover ? 0 : '100%' }}>
            <div tw='flex flex-row justify-between flex-wrap'>
              <div tw='mb-2'>
                <h5 tw="text-base font-bold">{nft.race.displayName}</h5>
                <p tw="text-sm">{nft.career.displayName}</p>
              </div>
              {/* {!hatched ? ( */}
              {/*   hasStartedIncubation ? ( */}
              {/*     // <div tw='text-xs text-[#84BFB5] mt-1'>Incubating</div> */}
              {/*     <div tw='text-xs text-[#84BFB5] mt-1'>Waiting to be Sent</div> */}
              {/*   ) : ( */}
              {/*     <div tw='text-xs text-[#957554] mt-1'>To be incubated</div> */}
              {/*   )) : null} */}
            </div>
            <div tw="flex flex-row justify-between mt-2 text-sm text-grey-300">
              <span>Gen {nft.generation}</span>
              {/* {fedCounts ? ( */}
              {/*   <span tw='inline-flex flex-row gap-1 items-center'> */}
              {/*     <FoodIcon tw='h-3 w-3 text-brand' />{fedCounts} */}
              {/*   </span> */}
              {/* ) : (<span />)} */}
            </div>
          </InfoPanel>
          <Box
            padding="0.25rem 0.25rem"
            backgroundColor={rarityColor}
            zIndex="2"
            position="relative"
            tw="flex flex-row justify-between"
          >
            <span tw="text-xs">{nft.rarity.displayName}</span>
            <span tw="text-xs">#{nft.id}</span>
          </Box>
        </div>
      </Box>
    </Link>
  );
}

export function LobbyCard({ nft }: { nft: UINft }) {
  const [isHover, setIsHover] = useState(false);
  const url = `/collections/${nft.collection.id}/${nft.id}`
  const rarityColor = nft.rarity.slug.toLowerCase() + '.500'
  return (
    <Link href={url} legacyBehavior>
      <Box
        cursor='pointer'
        border="1px solid"
        borderRadius="2px"
        borderColor={rarityColor}
        background="linear-gradient(180deg, rgba(91, 91, 91, 0.2) 0%, rgba(146, 146, 146, 0) 100%)"
        position="relative"
        minWidth="160px"
        minHeight="240px"
        css="aspect-ratio: 365/650"
        onMouseOver={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <div css={[tw`relative overflow-hidden`, 'aspect-ratio: 365/650;']}>
          {nft.previewImages?.background ? (
            <img
              src={imgproxy_thumbnail_url(nft.previewImages.background, 350, 800, 'jpg')}
              tw='h-full mx-auto absolute top-0 left-0'
            />
          ) : null}
          {nft.previewImages?.character ? (
            <img
              src={imgproxy_thumbnail_url(nft.previewImages?.character, 350, 800, 'webp')}
              tw='h-full mx-auto relative z-[1]'
            />
          ) : null}
        </div>
        <div css={[tw`absolute bottom-0 left-0 w-full overflow-hidden z-[2]`]}>
          <InfoPanel initial={false} animate={{/* translateY: isHover ? 0 : '100%' */}}>
            <div tw='flex flex-row justify-between flex-wrap'>
              <div css={[nft.listed ? tw`mb-2` : '']}>
                <h5 tw="text-base font-bold">Shell #{nft.id}</h5>
              </div>
            </div>
            {nft.listed ? (
            <div tw="flex flex-col justify-between mt-2 text-sm text-grey-300">
              <span tw='text-[#D4D4D8]'>Current Price</span>
              <span tw='font-bold text-base'>{formatCurrency(nft.listed.price.div(new BN(1e10)).toNumber() / 100)} PHA</span>
            </div>
            ) : null}
          </InfoPanel>
          <Box
            padding="0.25rem 0.25rem"
            backgroundColor={rarityColor}
            zIndex="2"
            position="relative"
            tw="flex flex-row justify-between"
          >
            <span tw="text-xs">{nft.rarity.displayName}</span>
          </Box>
          {nft.listed ? (
            <motion.div initial={false} animate={{translateY: isHover ? 0 : '100%'}} tw="absolute bottom-0 left-0 w-full z-[4]">
              <Button tw='w-full' fontWeight='900' padding="2.8rem 0">
                Buy Now
              </Button>
            </motion.div>
          ) : null}
        </div>
      </Box>
    </Link>
  );
}
